import { useEffect, useState } from "react";
import { Alert, Badge } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";
import { FilterBar } from "../../../components/molecule/molecule";
import ProjectHeader from "../../../components/ProjectHeader";
import {
    downloadCostTracking,
    useGetCostTrackingByIdQuery,
    useGetCostTrackingQuery,
    useGetCostTrackingVersionsQuery,
    useRefreshCostTrackingMutation,
} from "../../../store/services/costTrackingService";
import din276Service from "../../../store/services/din276Service";
import {
    selectSelectedVersionId,
    setSelectedVersion,
} from "../../../store/slices/costTrackingSlice";
import { selectProjectDetails } from "../../../store/slices/projectDetailsSlice";
import NoPermissionAlert from "../../../components/NoPermissionAlert";
import useProjectPermission from "../../../hooks/useProjectPermission";
import { getFormattedDate, showToast } from "../../../utils/method";
import { CostGroupHierarchy, CostSummaryCards, CostTrackingDownloadModal } from "./components";
import { PROJECT_AREA, PROJECT_ACTION } from "../../../constant/projectPermissions";
import "./cost-tracking.scss";

function CostTrackingPage() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { projectId } = useParams();

    const projectDetails = useSelector(selectProjectDetails);
    const selectedVersionId = useSelector(selectSelectedVersionId);
    const { can, isLoading: permissionLoading } = useProjectPermission();
    const canViewCostTracking =
        !permissionLoading && can(PROJECT_AREA.COST_TRACKING, PROJECT_ACTION.VIEW);
    const canExportCostTracking = can(PROJECT_AREA.COST_TRACKING, PROJECT_ACTION.EXPORT);
    const canEditCostTracking = can(PROJECT_AREA.COST_TRACKING, PROJECT_ACTION.EDIT);

    const skipDataQueries = !projectId || !canViewCostTracking;

    const {
        data: latestData,
        isLoading: loadingLatest,
        error: latestError,
    } = useGetCostTrackingQuery(projectId, { skip: skipDataQueries });

    const { data: versionsData } = useGetCostTrackingVersionsQuery(projectId, {
        skip: skipDataQueries,
    });

    const versions = versionsData?.versions ?? [];
    const latestVersionId = versions.find((v) => v.isLatest)?.id ?? null;

    // Viewing history = an explicit pick that isn't the latest snapshot
    const isViewingHistorical =
        selectedVersionId != null && selectedVersionId !== latestVersionId;

    const {
        data: historicalData,
        isLoading: loadingHistorical,
        error: historicalError,
    } = useGetCostTrackingByIdQuery(
        { projectId, costTrackingId: selectedVersionId },
        {
            skip:
                skipDataQueries || !isViewingHistorical || !selectedVersionId,
        },
    );

    const [refreshCostTracking] = useRefreshCostTrackingMutation();

    const activeData = isViewingHistorical ? historicalData : latestData;
    const hierarchy = activeData?.costTrackingData?.hierarchy;
    const summary = activeData?.costTrackingData?.summary;
    const costTrackingId = activeData?.id ?? null;
    const loading = loadingLatest || loadingHistorical;
    const error = latestError || historicalError;

    const [din276CostGroups, setDin276CostGroups] = useState([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showGross, setShowGross] = useState(true);
    const [showOnlyWithData, setShowOnlyWithData] = useState(true);

    const FILTER_ITEMS = [
        // Version picker: only meaningful when saved snapshots exist
        ...(versions.length > 0
            ? [
                {
                    key: "version",
                    type: "dropdown",
                    labelKey: "costTracking.selectVersion",
                    options: versions.map((version) => {
                        const date = getFormattedDate(new Date(version.createdAt));
                        const status = version.status;
                        const userName = version.createdBy?.name || t("common.unknown");
                        const currentBadge = version.isLatest ? ` (${t("costTracking.current")})` : "";
                        return {
                            value: version.id,
                            label: `${date} - ${status} - ${userName}${currentBadge}`,
                        };
                    }),
                    value: selectedVersionId || latestVersionId || "",
                    displayPosition: "left",
                    sortOptions: false,
                },
            ]
            : []),
        {
            key: "cost-groups-filter",
            type: "toggle",
            displayPosition: "right",
            options: [
                { label: t("costTracking.show-all-cost-groups"), value: false },
                { label: t("costTracking.show-data-only"), value: true },
            ],
            value: showOnlyWithData,
            onChange: (val) => setShowOnlyWithData(val),
        },
        ...(summary?.hasAnyGenerals === false
            ? [
                {
                    key: "gross-netto-toggle",
                    type: "toggle",
                    displayPosition: "right",
                    options: [
                        { label: t("costTracking.netto"), value: false },
                        { label: t("costTracking.brutto"), value: true },
                    ],
                    value: showGross,
                    onChange: (val) => setShowGross(val),
                },
            ]
            : []),
        // Refresh: only meaningful when there is a saved snapshot to regenerate
        ...(costTrackingId
            ? [
                {
                    key: "refresh",
                    type: "button",
                    labelKey: "common.refresh",
                    displayPosition: "right",
                    className: "btn btn-secondary",
                    icon: "refresh",
                    disabled: isRefreshing || isViewingHistorical,
                    loading: isRefreshing,
                },
            ]
            : []),
        ...(canExportCostTracking
            ? [
                  {
                      key: "download",
                      type: "button",
                      labelKey: "common.download",
                      displayPosition: "right",
                      icon: "download",
                  },
              ]
            : []),
    ];

    // When any generals are present, always force brutto (no toggle allowed)
    // When no generals, toggle is available but default remains brutto
    useEffect(() => {
        if (summary?.hasAnyGenerals === true) {
            setShowGross(true);
        }
    }, [summary?.hasAnyGenerals]);

    // Fetch DIN276 cost groups
    useEffect(() => {
        const fetchCostGroups = async () => {
            try {
                const response = await din276Service.getDIN276CostGroups();
                if (response?.items) {
                    setDin276CostGroups(response.items);
                }
            } catch (error) {
                console.error("Failed to fetch cost groups:", error);
                showToast("errors.INTERNAL_SERVER_ERROR", { type: "error" });
            }
        };
        fetchCostGroups();
    }, []);

    const onFilterAction = async (index, key, info) => {
        if (key === "version") {
            const versionId = info?.value;
            if (!versionId || !projectId) return;
            dispatch(setSelectedVersion(versionId));
            return;
        }

        if (key === "refresh") {
            if (isRefreshing) {
                return;
            }

            if (!projectId || !costTrackingId) {
                showToast(t("costTracking.refresh-error"), { type: "error" });
                return;
            }

            setIsRefreshing(true);
            try {
                await refreshCostTracking({ projectId, costTrackingId }).unwrap();
                showToast(t("costTracking.refresh-success"), { type: "success" });
            } catch (error) {
                showToast(error?.data?.message || error?.message || t("costTracking.refresh-error"), { type: "error" });
            } finally {
                setIsRefreshing(false);
            }
        } else if (key === "download") {
            setShowDownloadModal(true);
        }
    };

    const handleDownload = async ({ language, format, filename, onlyWithData, showGross }) => {
        if (!projectId) {
            showToast(t("costTracking.download-error"), { type: "error" });
            return;
        }
        setShowDownloadModal(false);
        try {
            await downloadCostTracking(
                projectId,
                { language, format, filename, onlyWithData, showGross },
                selectedVersionId,
            );
            showToast(t("costTracking.download-success"), { type: "success" });
        } catch (error) {
            showToast(error?.message || t("costTracking.download-error"), { type: "error" });
        }
    };

    if (permissionLoading) {
        return (
            <div className="cost-tracking-page">
                <ProjectHeader projectName={projectDetails?.name} tabTitle={t("costTracking.title")} />
                <div className="page-container">
                    <TableSkeleton rows={10} columns={6} />
                </div>
            </div>
        );
    }

    if (!canViewCostTracking) {
        return (
            <div className="cost-tracking-page">
                <ProjectHeader projectName={projectDetails?.name} tabTitle={t("costTracking.title")} />
                <NoPermissionAlert />
            </div>
        );
    }

    if (loading) {
        return (
            <div className="cost-tracking-page">
                <ProjectHeader projectName={projectDetails?.name} tabTitle={t("costTracking.title")} />
                <div className="page-container">
                    <div className="ct-skeleton">
                        <div className="ct-skeleton__cards">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <div key={i} className="ct-skeleton__card">
                                    <span className="sk-line" style={{ width: "55%", height: "10px" }} />
                                    <span className="sk-line" style={{ width: "75%", height: "24px" }} />
                                    <span className="sk-line" style={{ width: "40%", height: "9px" }} />
                                </div>
                            ))}
                        </div>
                        <div className="ct-skeleton__table">
                            <TableSkeleton rows={10} columns={6} />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        const errData = error?.data ?? error;
        const isNotFound = errData?.originalMessage === 'COST_TRACKING.NOT_FOUND';
        const errorMessage = typeof errData === 'string' ? errData : (errData?.message || JSON.stringify(errData));

        return (
            <Alert variant={isNotFound ? "info" : "danger"} className="m-4">
                <Alert.Heading>
                    {isNotFound
                        ? t("costTracking.not-found-title")
                        : t("common.error")}
                </Alert.Heading>
                <p>
                    {isNotFound
                        ? t("costTracking.not-found-message")
                        : errorMessage}
                </p>
            </Alert>
        );
    }

    return (
        <div className="cost-tracking-page">
            <ProjectHeader
                projectName={projectDetails?.name}
                tabTitle={t("costTracking.title")}
            />

            <div className="page-container">
                <FilterBar items={FILTER_ITEMS} onFilterAction={onFilterAction} />

                {/* Historical Data Warning */}
                {isViewingHistorical && (
                    <Alert variant="warning" className="mx-2 mt-3">
                        <Badge bg="warning" text="dark" className="me-2">
                            {t("common.warning")}
                        </Badge>
                        {t("costTracking.viewingHistoricalData")}
                    </Alert>
                )}

                {/* Summary Cards */}
                <CostSummaryCards summary={summary} showGross={showGross} />

                <div className="cost-tracking-content">
                    <CostGroupHierarchy
                        hierarchy={hierarchy}
                        summary={summary}
                        costTrackingId={costTrackingId}
                        costGroups={din276CostGroups}
                        showGross={showGross}
                        showOnlyWithData={showOnlyWithData}
                        canEdit={canEditCostTracking}
                    />
                </div>
            </div>

            <CostTrackingDownloadModal
                show={showDownloadModal}
                onClose={() => setShowDownloadModal(false)}
                onDownload={handleDownload}
                projectName={projectDetails?.name}
                hasAnyGenerals={summary?.hasAnyGenerals}
            />
        </div>
    );
}

export default CostTrackingPage;
